import api from "@/lib/axiosConfig";
import { SculptureEdition } from "@/lib/types";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";

// ─── DTOs ────────────────────────────────────────────────────────────────────

interface SculptureEditionListDTO {
  where: object;
  order: object[];
  take: number;
  skip: number;
}

export async function countSculptureEditionsByStatus(
  masterArtworkId: string,
  where?: object,
): Promise<{ published: number; draft: number }> {
  const { data } = await api.post<{ published: number; draft: number }>(
    `back/api/sculpture-edition/countByStatus/${masterArtworkId}`,
    { where },
  );
  return data;
}

// ─── GET LIST ─────────────────────────────────────────────────────────────────

async function getSculptureEditions({
  where,
  order,
  take,
  skip,
}: SculptureEditionListDTO): Promise<SculptureEdition[]> {
  const { data } = await api.post<SculptureEdition[]>(
    `back/api/sculpture-edition?take=${take || 10}&skip=${skip || 0}`,
    { where, orderBy: order },
  );
  return data;
}

export const useGetSculptureEditions = (
  masterArtworkId: string,
  options: Omit<SculptureEditionListDTO, "where"> & { where?: object },
) => {
  const mergedWhere = {
    ...options.where,
    masterArtworkId,
  };
  return useQuery({
    queryKey: [
      "sculptureEditions",
      masterArtworkId,
      mergedWhere,
      options.order,
      options.take,
      options.skip,
    ],
    queryFn: () =>
      getSculptureEditions({
        where: mergedWhere,
        order: options.order,
        take: options.take,
        skip: options.skip,
      }),
    placeholderData: keepPreviousData,
    enabled: !!masterArtworkId,
  });
};

// ─── GET BY ID ────────────────────────────────────────────────────────────────

async function getSculptureEditionById(id: string): Promise<SculptureEdition> {
  const { data } = await api.get<SculptureEdition>(
    `back/api/sculpture-edition/${id}`,
  );
  return data;
}

export const useGetSculptureEditionById = (id: string | undefined): UseQueryResult<SculptureEdition, Error> => {
  return useQuery({
    queryKey: ["sculptureEdition", id],
    queryFn: () => getSculptureEditionById(id!),
    enabled: !!id,
  });
};

// ─── GET RELATED FIELDS (public-shaped, used by the overview preview) ──────────

async function getSculptureEditionRelatedFields(id: string): Promise<any> {
  const { data } = await api.get<any>(
    `back/api/sculpture-edition/public/related/${id}`,
  );
  return data ?? {};
}

export const useGetSculptureEditionRelatedFields = (
  id: string | undefined,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: ["sculptureEditionRelated", id],
    queryFn: () => getSculptureEditionRelatedFields(id!),
    enabled: !!id && (options?.enabled ?? true),
  });
};

// ─── GET FOR PREVIEW (BO, with all relations) ─────────────────────────────────

async function getSculptureEditionForPreview(id: string): Promise<any> {
  const { data } = await api.get<any>(
    `back/api/sculpture-edition/preview/${id}`,
  );
  return data;
}

export const useGetSculptureEditionForPreview = (
  id: string | undefined,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: ["sculptureEditionPreview", id],
    queryFn: () => getSculptureEditionForPreview(id!),
    enabled: !!id && (options?.enabled ?? true),
  });
};

// ─── CREATE ───────────────────────────────────────────────────────────────────

export type CreateSculptureEditionParams = {
  masterArtworkId: string;
  sculptureEditionNumeroEnregistrement: string;
  sculptureEditionPublicationStatus?: string;
  copyFromMaster?: boolean;
};

async function createSculptureEdition(
  params: CreateSculptureEditionParams,
): Promise<SculptureEdition> {
  const { data } = await api.post<SculptureEdition>(
    `back/api/sculpture-edition/create`,
    params,
  );
  return data;
}

export const useCreateSculptureEdition = () => {
  const queryClient = useQueryClient();
  return useMutation<SculptureEdition, Error, CreateSculptureEditionParams>({
    mutationFn: createSculptureEdition,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["sculptureEditions"] });
      queryClient.invalidateQueries({
        queryKey: ["sculptureEdition", data.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["countByStatus", "sculptureEdition"],
        exact: false,
      });
    },
  });
};

// ─── UPDATE ───────────────────────────────────────────────────────────────────

export type UpdateSculptureEditionParams = {
  data: Partial<CreateSculptureEditionParams> & Record<string, unknown>;
  where: { id: string };
};

async function updateSculptureEdition(
  params: UpdateSculptureEditionParams,
): Promise<SculptureEdition> {
  const { data } = await api.put<SculptureEdition>(
    `back/api/sculpture-edition/${params.where.id}`,
    params.data,
  );
  return data;
}

export const useUpdateSculptureEdition = () => {
  const queryClient = useQueryClient();
  return useMutation<SculptureEdition, Error, UpdateSculptureEditionParams>({
    mutationFn: updateSculptureEdition,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["sculptureEditions"] });
      queryClient.invalidateQueries({
        queryKey: ["sculptureEdition", data.id],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["countByStatus", "sculptureEdition"],
        exact: false,
      });
    },
  });
};

// ─── DELETE ───────────────────────────────────────────────────────────────────

async function deleteSculptureEdition(id: string): Promise<void> {
  await api.post(`back/api/sculpture-edition/delete`, { where: { id } });
}

export const useDeleteSculptureEdition = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSculptureEdition,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sculptureEditions"] });
      queryClient.invalidateQueries({
        queryKey: ["countByStatus", "sculptureEdition"],
        exact: false,
      });
    },
  });
};

// ─── DUPLICATE ────────────────────────────────────────────────────────────────

export type DuplicateSculptureEditionParams = {
  sourceId: string;
  sculptureEditionNumeroEnregistrement: string;
};

async function duplicateSculptureEdition(
  params: DuplicateSculptureEditionParams,
): Promise<SculptureEdition> {
  const { data } = await api.post<SculptureEdition>(
    `back/api/sculpture-edition/${params.sourceId}/duplicate`,
    {
      sculptureEditionNumeroEnregistrement:
        params.sculptureEditionNumeroEnregistrement,
    },
  );
  return data;
}

export const useDuplicateSculptureEdition = () => {
  const queryClient = useQueryClient();
  return useMutation<SculptureEdition, Error, DuplicateSculptureEditionParams>({
    mutationFn: duplicateSculptureEdition,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["sculptureEditions"] });
      queryClient.invalidateQueries({
        queryKey: ["sculptureEdition", data.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["countByStatus", "sculptureEdition"],
        exact: false,
      });
    },
  });
};
